import { filter, isEmpty } from 'lodash';
import { useMemo } from 'react';

import { Bank, DeviceOS, PaymentType } from 'enums';
import { Deeplink, Order, TradeMethod } from 'types';
import { userDeviceUtils } from 'utils';

type UseDeeplinksInput = {
  order: Order | undefined;
  selectedTradeMethod: TradeMethod | undefined;
  customerBankCode: string;
};

const VIETNAM_SBER_QR_DEEPLINK: Deeplink = {
  id: 'vietnam-sber-qr-fallback',
  customerBank: Bank.Sberbank,
  deviceOs: DeviceOS.Android,
  url: 'intent://ru.sberbankmobile/qrpay?qrPayLinkKey={{payment_string_vietnam_sber_qr}},android-app://ru.sberbankmobile/qrpay?qrPayLinkKey={{payment_string_vietnam_sber_qr}}',
};

export const useDeeplinks = ({
  order,
  selectedTradeMethod,
  customerBankCode,
}: UseDeeplinksInput): Deeplink[] => {
  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);

  const tradeMethodDeeplinks = useMemo<Deeplink[]>(
    () =>
      filter(
        selectedTradeMethod?.deeplinks,
        (deeplink) =>
          !!deeplink.url &&
          (!customerBankCode || deeplink.customerBank === customerBankCode) &&
          (!deeplink.deviceOs || deeplink.deviceOs === deviceOS),
      ),
    [selectedTradeMethod?.deeplinks, customerBankCode, deviceOS],
  );

  const vietnamSberQrFallbackDeeplinks = useMemo<Deeplink[]>(
    () =>
      order?.payment?.type === PaymentType.NSPK &&
      !!order?.requisites?.paymentStringVietnamSberQR &&
      isEmpty(tradeMethodDeeplinks) &&
      VIETNAM_SBER_QR_DEEPLINK.deviceOs === deviceOS
        ? [VIETNAM_SBER_QR_DEEPLINK]
        : [],
    [
      deviceOS,
      order?.payment?.type,
      order?.requisites?.paymentStringVietnamSberQR,
      tradeMethodDeeplinks,
    ],
  );

  return !isEmpty(tradeMethodDeeplinks)
    ? tradeMethodDeeplinks
    : vietnamSberQrFallbackDeeplinks;
};
